import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Text, Spinner, Buttons } from '@taxdown/piccolo-web';
import { locizeNS } from 'Services/i18n';
import { history } from 'Store';
import { routeStateSelector } from 'Store/modules/router/selectors';
import { useTranslation } from 'Hooks/useTranslation';
import { useFetchGetPlans } from 'Containers/Plans/hooks';
import { createURL } from 'Containers/Plans/utils';
import { getResultIsPayLater, TaxReturnTypes } from 'Containers/Result/utils';
import FlowColLayout from 'Components/Layouts/FlowColLayout';
import CustomTransition from 'Components/CustomTransition';
import UpsellingCard from 'Components/UpsellingCard';
import { FRONT_PLANS, parsePlans, PLAN_SHORT_NAME_DICTIONARY } from 'Constants/plans';
import { routes } from 'Constants/routes';
import { PlansType } from 'Types/common';
import * as Styled from './styles';

const NOT_TO_RETURN_CONTEXT = 'notToReturn';

const UpsellingContainer = (): JSX.Element => {
  const { t } = useTranslation(locizeNS.RESULT);
  const { data: plans, isLoading: loading } = useFetchGetPlans();
  const upsellingProps = useSelector(routeStateSelector);
  const [data, setData] = useState<PlansType[]>();
  const [payLater, setPayLater] = useState<PlansType>();

  const existUpsellingProps =
    !upsellingProps ||
    !upsellingProps.route ||
    !upsellingProps.cancelUrl ||
    !upsellingProps.successUrl ||
    !upsellingProps.section ||
    !upsellingProps.result;

  useEffect(() => {
    if (existUpsellingProps) {
      history.push(routes.RESULT);
    }
  }, [upsellingProps]);

  useEffect(() => {
    if (plans) {
      setData(plans.filter(item => parsePlans(item.name) !== FRONT_PLANS.FLEX));
      setPayLater(plans.filter(item => parsePlans(item.name) === FRONT_PLANS.FLEX)[0]);
    }
  }, [plans]);

  const renderButtonPayLaterOption =
    payLater &&
    upsellingProps?.result?.taxReturnType === TaxReturnTypes.TO_RETURN &&
    getResultIsPayLater(
      upsellingProps?.result?.taxReturnResult,
      parseInt(payLater.price.amountDecimal, 10)
    ) &&
    Math.abs(upsellingProps?.result?.taxReturnResult) < 9999;

  const handleClickPayLater = (): void => {
    history.push(routes.PAYMENT_LATER, {
      route: routes.RESULT,
      section: 'result',
      taxReturnType: upsellingProps?.result?.taxReturnType,
      taxReturnResult: upsellingProps?.result?.taxReturnResult,
      instantRefund: upsellingProps?.refund,
    });
  };

  const tContext =
    upsellingProps?.result?.taxReturnType !== TaxReturnTypes.TO_RETURN ? NOT_TO_RETURN_CONTEXT : '';

  return (
    <FlowColLayout section={upsellingProps?.section} isFlow>
      <CustomTransition start>
        {!data || loading ? (
          <Styled.SpinnerContainer align="center" justify="center">
            <Spinner size="M" justify="center" align="center" />
          </Styled.SpinnerContainer>
        ) : (
          <Styled.Container direction="column" align="center" justify="center" gap="dimension_16">
            <Text type="paragraphs" color="dark" size="XL_XL_S" align="center">
              {t('Result.Upselling.title', { context: tContext })}
            </Text>
            <Text type="paragraphs" color="dark" size="XS_L_M" align="center">
              {t('Result.Upselling.subtitle')}
            </Text>
            <Styled.CardContainer>
              {data.map(
                item =>
                  item.name !== PLAN_SHORT_NAME_DICTIONARY.BASIC && (
                    <UpsellingCard
                      key={item.id}
                      upsellingProps={item}
                      successUrl={createURL(upsellingProps.successUrl, {
                        filing: 'true',
                        plans: parsePlans(item.name),
                      })}
                      cancelUrl={createURL(upsellingProps.cancelUrl)}
                      result={upsellingProps.result}
                    />
                  )
              )}
            </Styled.CardContainer>
            {renderButtonPayLaterOption && (
              <Styled.CTA justify="center">
                <Buttons.Classic
                  size="l"
                  mode="success"
                  label={t('Plans.payLater.second.option')}
                  onClick={handleClickPayLater}
                />
              </Styled.CTA>
            )}
          </Styled.Container>
        )}
      </CustomTransition>
    </FlowColLayout>
  );
};

export default UpsellingContainer;
